// payoutMethod.js
import FormPage from '@/components/FormPage'
import { createGetParams, createPostParams } from '@/rest'
const formTitle = 'Payment Method & Bank Info'

export default {
  extends: FormPage,
  name: 'PayoutMethod',
  data () {
    return {
      title: formTitle,
      breadcrumbs: [
        { text: formTitle, disabled: true },
      ],
      thirdPartyRequest: null,
      profileType: null,
      status: null,
      alertText: null,
      locked: false,
      pendingApproval: false,
      rejectionReason: null,
      consentFormUrl: (process.env.BASE_URL || '/') + 'consent_form.pdf',
      profile: {
        name: null,
        companyName: null,
        id: null, // individual NRIC
        roc: null,
      },

      // NOTE: we no longer rely on a single consentUrls array.
      // Each upload field keeps its own `field.value` (with .url / .name).
      // We'll still track the list of uploaded files if needed:
      uploadsStore: {},

      // Form steps
      selection: {
        title: 'Payment method',
        subTitle: 'Choose payout method',
        fields: {
          thirdPartyStatusInfo: {
            component: () => import('@/components/ThirdPartyStatusInfo'),
            name: '3rd Party Status',
            value: null, // ✅ REQUIRED
            info: null, // ✅ REQUIRED
            hide: true, // ✅ start hidden
            props: {}, // ✅ REQUIRED for form-field mixin
          },

          paymentMethod: {
            component: () => import('@/components/SelectField'),
            name: 'Payment Method',
            props: {
              label: 'Payment method',
              'item-text': 'code',
              'item-value': 'value',
              items: [
                { code: 'Cash', value: 'cash' },
              ],
              readonly: true,
            },
            value: 'cash',
          },
          accountType: {
            component: () => import(/* webpackChunkName: "selectField" */ '@/components/SelectField'),
            name: 'Type',
            hide: true,
            props: {
              label: 'Select account type',
              'item-text': 'code',
              'item-value': 'value',
              items: [
                { code: 'Individual', description: 'Personal account', value: 'individual' },
                { code: 'Company', description: 'Company account', value: 'company' },
              ],
              rules: [
                v => !!v || 'Account type is required',
              ],
              required: true,
            },
          },
          payoutTo: {
            component: () => import(/* webpackChunkName: "selectField" */ '@/components/SelectField'),
            name: 'Incentive Payout',
            hide: true,
            props: {
              label: 'Select payout destination',
              'item-text': 'code',
              'item-value': 'value',
              items: [
                { code: 'Own Account', description: 'Payout to your own account', value: 'own' },
                { code: '3rd Party Account', description: 'Payout to a 3rd party account', value: 'third_party' },
              ],
              rules: [v => this.paymentMethod !== 'cash' || !!v || 'Payout selection is required'],
              required: true,
            },
            value: 'own', // ✅ DEFAULT TO OWN ACCOUNT
          },

        },
      },

      ownAccount: {
        title: 'Own account details',
        subTitle: 'Verify and complete bank details',
        fields: {
          ownerName: {
            component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
            name: 'Name / Company Name',
            props: {
              readonly: true,
              label: 'Auto-filled from your profile',
            },
          },
          ownerId: {
            component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
            name: 'NRIC / BRN',
            props: {
              readonly: true,
              label: 'Auto-filled from your profile',
            },
          },
          bankName: {
            component: () => import('@/components/SelectField'),
            name: 'Bank Name',
            props: {
              label: 'Select bank',
              'item-text': 'name',
              'item-value': 'key',
              items: [], // will be loaded from API
              rules: [v => !this.requiresOwnAccount || !!v || 'Bank name is required'],
              required: false,
            },
            value: null,
          },

          bankAccount: {
            component: () => import('@/components/TextField'),
            name: 'Bank Account Number',
            props: {
              label: 'Enter bank account number',
              rules: [
                v => !this.requiresOwnAccount || !!v || 'Bank account number is required',
                v => !this.requiresOwnAccount || this.isNumeric(v) || 'Digits only, no symbols or spaces',
              ],
            },
            value: null, // ✅ CORRECT PLACE
          },

        },
      },

      // ---------- thirdPartyDocs: EXACTLY THE 6 CATEGORIES ----------
      thirdPartyDocs: {
        title: '3rd party documents',
        subTitle: 'Upload required documents',
        fields: {

          // ---------- CONSENT DOWNLOAD ----------
          consentLetterDownload: {
            component: () => import('@/components/ConsentDownload'),
            name: 'Consent Form',
            props: {
              url: 'http://onesys.onexox.my/form/consent_3rd_party.pdf',
              helpLabel: 'How to merge PDF',
            },
          },

          // ---------- CONSENT UPLOAD ----------
          consentLetterUpload: {
            component: () => import('@/components/upload3rdParty2'),
            name: 'Signed Consent Form',
            hide: true,
            value: null,
            props: {
              label: 'Upload signed consent form (PDF)',
              // rules: [() => this.validateUpload('consentLetterUpload')],
              fieldKey: 'consentLetterUpload',
              title: 'Signed Consent Form',
              autoUpload: true,
            },
          },

          // ---------- INDIVIDUAL ----------
          thirdPartyIc: {
            component: () => import('@/components/upload3rdParty2'),
            name: '3rd Party IC (Front & Back)',
            hide: true,
            value: null,
            props: {
              label: 'Upload 3rd party IC (front & back in ONE PDF)',
              // rules: [() => this.validateUpload('thirdPartyIc')],
              fieldKey: 'thirdPartyIc',
              title: '3rd Party IC',
              autoUpload: true,
            },
          },

          witnessIc: {
            component: () => import('@/components/upload3rdParty2'),
            name: 'Witness IC (Front & Back)',
            hide: true,
            value: null,
            props: {
              label: 'Upload witness IC (front & back in ONE PDF)',
              // rules: [() => this.validateUpload('witnessIc')],
              fieldKey: 'witnessIc',
              title: 'Witness IC',
              autoUpload: true,
            },
          },

          // ---------- COMPANY ----------
          form49: {
            component: () => import('@/components/upload3rdParty2'),
            name: 'Form 49',
            hide: true,
            value: null,
            props: {
              label: 'Upload Form 49',
              // rules: [() => this.validateUpload('form49')],
              fieldKey: 'form49',
              title: 'Form 49',
              autoUpload: true,
            },
          },

          form51: {
            component: () => import('@/components/upload3rdParty2'),
            name: 'Form 51',
            hide: true,
            value: null,
            props: {
              label: 'Upload Form 51',
              // rules: [() => this.validateUpload('form51')],
              fieldKey: 'form51',
              title: 'Form 51',
              autoUpload: true,
            },
          },

          form58: {
            component: () => import('@/components/upload3rdParty2'),
            name: 'Form 58',
            hide: true,
            value: null,
            props: {
              label: 'Upload Form 58',
              // rules: [() => this.validateUpload('form58')],
              fieldKey: 'form58',
              title: 'Form 58',
              autoUpload: true,
            },
          },

          ssm: {
            component: () => import('@/components/upload3rdParty2'),
            name: 'SSM',
            hide: true,
            value: null,
            props: {
              label: 'Upload SSM / Business Registration Certificate',
              // rules: [() => this.validateUpload('ssm')],
              fieldKey: 'ssm',
              title: 'SSM',
              autoUpload: true,
            },
          },
        },
      },

      thirdPartyBank: {
        title: '3rd party bank details',
        subTitle: 'Provide 3rd party account information',
        fields: {
          tpName: {
            component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
            name: 'Account Holder Name',
            props: {
              label: 'Enter account holder name',
              rules: [v => !this.requiresThirdParty || !!v || 'Account holder name is required'],
              required: false,
            },
            value: null,
          },
          tpId: {
            component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
            name: 'NRIC / BRN',
            props: {
              label: 'Enter NRIC/BRN',
              rules: [v => !this.requiresThirdParty || !!v || 'NRIC/BRN is required'],
              required: false,
            },
            value: null,
          },
          tpBankName: {
            component: () => import('@/components/SelectField'),
            name: 'Bank Name',
            props: {
              label: 'Select bank',
              'item-text': 'name',
              'item-value': 'key',
              items: [], // loaded from API
              rules: [v => !this.requiresThirdParty || !!v || 'Bank name is required'],
              required: false,
            },
            value: null,
          },
          tpBankAccount: {
            component: () => import(/* webpackChunkName: "textField" */ '@/components/TextField'),
            name: 'Bank Account Number',
            props: {
              label: 'Enter bank account number',
              rules: [
                v => !this.requiresThirdParty || !!v || 'Bank account number is required',
                v => !this.requiresThirdParty || this.isNumeric(v) || 'Digits only, no symbols or spaces',
              ],
              required: false,
            },
            value: null,
          },
        },
      },

      submission: {
        title: 'Submit',
        subTitle: 'Confirm and submit your update',
        submit: false,
        status: null,
        errorMessage: null,
        fields: {
          summary: {
            name: 'Summary',
            component: () => import(/* webpackChunkName: "readOnlyField" */ '@/components/ReadOnlyField'),
            props: {
              label: 'Review details before submission',
            },
            value: '',
          },
        },
      },
    }
  },

  computed: {
    isPending () {
      return this.pendingApproval === true
    },
    thirdPartyStatusLabel () {
      if (!this.thirdPartyRequest) return null

      if (this.thirdPartyRequest.status === 'pending') {
        return 'Pending 3rd Party Approval'
      }

      if (this.thirdPartyRequest.status === 'success') {
        return '3rd Party Approved'
      }

      if (this.thirdPartyRequest.status === 'reject') {
        return '3rd Party Rejected'
      }

      return null
    },
    showThirdPartyInfo () {
      return !!this.thirdPartyRequest
    },

    thirdPartyAlertType () {
      if (!this.thirdPartyRequest) return 'info'
      if (this.thirdPartyRequest.status === 'pending') return 'info'
      if (this.thirdPartyRequest.status === 'success') return 'success'
      if (this.thirdPartyRequest.status === 'reject') return 'error'
      return 'info'
    },

    thirdPartyStatusText () {
      if (!this.thirdPartyRequest) return ''

      const { status, createdAt } = this.thirdPartyRequest

      if (status === 'pending') {
        return `Your 3rd party payout request submitted on ${createdAt} is pending DMT approval.`
      }

      if (status === 'success') {
        return 'Your 3rd party payout request has been approved.'
      }

      if (status === 'reject') {
        return 'Your 3rd party payout request was rejected.'
      }

      return ''
    },
    formSteps () {
      const hasStatus = !!this.thirdPartyRequest

      // 🔒 Pending → show ONLY status
      if (this.isPending) {
        return {
          selection: {
            ...this.selection,
            fields: {
              thirdPartyStatusInfo: this.selection.fields.thirdPartyStatusInfo,
            },
          },
        }
      }

      // ❌ Rejected / ✅ Approved → show status + allow form
      if (hasStatus) {
        return {
          selection: {
            ...this.selection,
            fields: {
              thirdPartyStatusInfo: this.selection.fields.thirdPartyStatusInfo,
              paymentMethod: this.selection.fields.paymentMethod,
              accountType: this.selection.fields.accountType,
              payoutTo: this.selection.fields.payoutTo,
            },
          },
          ...(this.payoutTo === 'third_party'
            ? {
              thirdPartyDocs: this.thirdPartyDocs,
              thirdPartyBank: this.thirdPartyBank,
            }
            : {
              ownAccount: this.ownAccount,
            }),
          submission: this.submission,
        }
      }

      // 🟢 Normal first-time flow
      if (this.payoutTo === 'own') {
        return {
          selection: this.selection,
          ownAccount: this.ownAccount,
          submission: this.submission,
        }
      }

      if (this.payoutTo === 'third_party') {
        return {
          selection: this.selection,
          thirdPartyDocs: this.thirdPartyDocs,
          thirdPartyBank: this.thirdPartyBank,
          submission: this.submission,
        }
      }

      return {
        selection: this.selection,
        submission: this.submission,
      }
    },
    paymentMethod () {
      return this.selection.fields.paymentMethod.value
    },
    accountType () {
      return this.selection.fields.accountType.value
    },
    payoutTo () {
      return this.selection.fields.payoutTo.value
    },
    requiresOwnAccount () {
      return this.paymentMethod === 'cash' && this.payoutTo === 'own'
    },
    requiresThirdParty () {
      return this.paymentMethod === 'cash' && this.payoutTo === 'third_party'
    },
  },

  watch: {
    paymentMethod () {
      this.syncVisibility()
      this.updateSummary()
    },
    accountType () {
      this.applyPayoutRules()
      this.syncVisibility()
      this.prefillOwner()
      this.updateSummary()
    },
    payoutTo () {
      this.syncVisibility()
      this.updateSummary()
    },
    'ownAccount.fields.bankName.value' () {
      this.updateSummary()
    },
    'ownAccount.fields.bankAccount.value' () {
      this.updateSummary()
    },
    'thirdPartyBank.fields.tpName.value' () {
      this.updateSummary()
    },
    'thirdPartyBank.fields.tpId.value' () {
      this.updateSummary()
    },
    'thirdPartyBank.fields.tpBankName.value' () {
      this.updateSummary()
    },
    'thirdPartyBank.fields.tpBankAccount.value' () {
      this.updateSummary()
    },
  },

  mounted: function () {
    this.syncVisibility()
    this.loadProfile()
    this.loadBanks()
    this.loadExistingRequest()
    this.$root.$on('help-click', this.onHelpClick)

    this.$root.$on('uploaded-ready', this.onUploadedReady)
  },

  beforeDestroy () {
    try {
      this.$root.$off('uploaded-ready', this.onUploadedReady)
    } catch (err) {
      console.warn('failed to unbind uploaded-ready', err)
    }
  },

  methods: {
    applyPayoutRules () {
      const payoutField = this.selection.fields.payoutTo

      // reset items
      payoutField.props.items = [
        { code: 'Own Account', value: 'own' },
        { code: '3rd Party Account', value: 'third_party' },
      ]

      payoutField.props.readonly = false

      // ❗ mismatch → ONLY 3rd party
      if (
        this.profileType &&
    this.accountType &&
    this.profileType !== this.accountType
      ) {
        payoutField.props.items = [
          { code: '3rd Party Account', value: 'third_party' },
        ]
        payoutField.value = 'third_party'
        payoutField.props.readonly = true
      }
    },
    onHelpClick ({ fieldKey }) {
      if (fieldKey === 'consentLetterDownload') {
        this.openMergeHelp()
      }
    },

    openMergeHelp () {
      this.$root.$emit('open-global-dialog', {
        title: 'How to merge files into one PDF',
        content: `
        <ol>
          <li>Take photo or scan IC (front)</li>
          <li>Take photo or scan IC (back)</li>
          <li>Use one app:
            <ul>
              <li>Adobe Scan</li>
              <li>CamScanner</li>
              <li>Google Drive → Scan → Save as PDF</li>
            </ul>
          </li>
          <li>Save as <strong>ONE PDF</strong></li>
          <li>Upload the PDF here</li>
        </ol>
        <p><strong>Note:</strong> Only one file is allowed.</p>
      `,
      })
    },

    // put this inside methods: { ... }
    titleToFieldKey (title) {
      if (!title) return null
      const t = String(title).trim().toLowerCase()

      // common mappings — expand if you see other title patterns in logs
      if (t.includes('consent') || t.includes('kebenaran') || t.includes('consent letter')) return 'consentLetterUpload'
      if ((t.includes('third') && t.includes('ic')) || (t.includes('third party') && t.includes('ic')) || t.includes('third party ic')) return 'thirdPartyIc'
      if (t.includes('witness')) return 'witnessIc'
      if (t.includes('authorization') || t.includes('authorisation') || t.includes('letter of authorization') || t.includes('authorization letter') || t.includes('auth')) return 'letterAuthorization'
      if (t.includes('form') && t.includes('59')) return 'form59'
      if (t.includes('ssm') || t.includes('business registration') || t.includes('registration certificate')) return 'ssm'

      // // fallback common keywords
      // if (t.includes('ic') && t.includes('front')) return 'thirdPartyIc'
      // if (t.includes('ic') && t.includes('witness')) return 'witnessIc'

      return null
    },

    onUploadedReady (payload) {
      if (!payload || !payload.fieldKey) return

      const field = this.thirdPartyDocs.fields[payload.fieldKey]
      if (!field) return

      const url = payload.urls?.[0] || null
      const file = payload.files?.[0] || {}

      const originalFilename =
    file.name ||
    file.meta?.name ||
    payload.fieldKey + '.pdf'

      // ✅ ALWAYS overwrite name + url
      field.value = {
        ...(field.value || {}),
        url,
        name: originalFilename,
      }

      console.log('[PAYOUT] stored:', payload.fieldKey, field.value)
      this.updateSummary()
    },
    onChildUploaded (payload) {
      if (!payload || !payload.fieldKey) return
      const field = this.thirdPartyDocs.fields[payload.fieldKey]
      if (!field) return
      field.value = payload.fileObj
      // update summary / buildUploadPayload will pick this up
      this.updateSummary()
    },
    // validation for the six upload keys
    validateUpload (fieldKey, companyOnly = false) {
      if (!this.requiresThirdParty) return true
      if (companyOnly && this.accountType !== 'company') return true

      // 🚫 Skip DOWNLOAD field completely
      if (fieldKey === 'consentLetterDownload') return true

      const field = this.thirdPartyDocs.fields[fieldKey]
      if (!field || field.hide) return true

      const val = field.value
      if (val && typeof val === 'object' && val.url) return true

      return 'This document is required'
    },

    syncVisibility () {
      if (this.pendingApproval) {
        return
      }
      const isCash = this.paymentMethod === 'cash'
      const isThirdParty = this.payoutTo === 'third_party'
      const isIndividual = this.accountType === 'individual'
      const isCompany = this.accountType === 'company'

      this.selection.fields.accountType.hide = !isCash
      this.selection.fields.payoutTo.hide = !isCash

      // Hide all docs by default
      Object.values(this.thirdPartyDocs.fields).forEach(field => {
        if (field && Object.prototype.hasOwnProperty.call(field, 'hide')) {
          field.hide = true
        }
      })

      // Only show docs when CASH + 3rd party
      if (!isCash || !isThirdParty) return

      // Common (both)
      this.thirdPartyDocs.fields.consentLetterUpload.hide = false

      // INDIVIDUAL
      if (isIndividual) {
        this.thirdPartyDocs.fields.thirdPartyIc.hide = false
        this.thirdPartyDocs.fields.witnessIc.hide = false
      }

      // COMPANY
      if (isCompany) {
        this.thirdPartyDocs.fields.form49.hide = false
        this.thirdPartyDocs.fields.form51.hide = false
        this.thirdPartyDocs.fields.form58.hide = false
        this.thirdPartyDocs.fields.ssm.hide = false
      }
    },

    async loadBanks () {
      try {
        const params = createGetParams({})
        const response = await this.$rest.get('getBanks.php', params)

        let banks = (response.data || []).map(b => ({
          key: String(b.key),
          name: b.name,
        }))

        // ⭐ Sort alphabetically A–Z
        banks = banks.sort((a, b) => a.name.localeCompare(b.name))

        // Assign sorted list
        this.ownAccount.fields.bankName.props.items = banks
        this.thirdPartyBank.fields.tpBankName.props.items = banks
      } catch (error) {
        console.error('Failed to load bank list:', error)
      }
    },

    getBankNameById (id) {
      const list = this.ownAccount.fields.bankName.props.items
      const match = list.find(b => String(b.key) === String(id))
      return match ? match.name : '-'
    },

    isNumeric (value) {
      return !!value && /^[0-9]+$/.test(value)
    },

    prefillOwner () {
      const fields = this.ownAccount.fields

      if (this.accountType === 'company') {
        fields.ownerName.props.label = 'Company name (auto-filled)'
        fields.ownerName.value = this.profile.companyName || this.profile.name

        // ✅ COMPANY → TAKE ROC
        fields.ownerId.props.label = 'BRN (auto-filled)'
        fields.ownerId.value = this.profile.roc
      } else {
        fields.ownerName.props.label = 'Name (auto-filled)'
        fields.ownerName.value = this.profile.name

        // ✅ INDIVIDUAL → TAKE ID
        fields.ownerId.props.label = 'NRIC (auto-filled)'
        fields.ownerId.value = this.profile.id
      }
    },

    async loadProfile () {
      try {
        const params = createGetParams({ profiling: true })
        const response = await this.$rest.get('getProfile.php', params)

        const data = response.data || {}

        this.profile.name = data.name || null
        this.profile.companyName = data.rocName || null

        // 🔥 Important mapping
        this.profile.id = data.id || null // Individual NRIC
        this.profile.roc = data.roc || null // Company ROC / BRN

        this.prefillOwner()
        this.updateSummary()
      } catch (error) {
        console.error('Failed to load profile:', error)
      }
    },

    async loadExistingRequest () {
      try {
        const params = createGetParams({})
        const response = await this.$rest.get('getPayoutMethod.php', params)
        const data = response.data || {}
        this.locked = data.locked || false
        this.profileType = data.profileType || null
        this.pendingApproval = data.thirdPartyRequest && data.thirdPartyRequest.status === 'pending'
        this.rejectionReason = data.rejectionReason
        this.selection.fields.paymentMethod.value = 'cash'
        this.selection.fields.accountType.value = data.accountType || null
        this.selection.fields.payoutTo.value = data.payoutTo || null
        this.ownAccount.fields.bankName.value = data.bankName ? String(data.bankName) : null
        this.ownAccount.fields.bankAccount.value = data.bankAccount || null
        this.ownAccount.fields.ownerName.value = data.ownerName || this.ownAccount.fields.ownerName.value
        this.ownAccount.fields.ownerId.value = data.ownerId || this.ownAccount.fields.ownerId.value
        this.thirdPartyBank.fields.tpName.value = data.tpName || null
        this.thirdPartyBank.fields.tpId.value = data.tpId || null
        this.thirdPartyBank.fields.tpBankName.value = data.tpBankName ? String(data.tpBankName) : null
        this.thirdPartyBank.fields.tpBankAccount.value = data.tpBankAccount || null
        this.thirdPartyRequest = data.thirdPartyRequest || null

        if (this.thirdPartyRequest) {
          const field = this.selection.fields.thirdPartyStatusInfo
          const { status, createdAt, rejectionReason } = this.thirdPartyRequest

          this.$set(field, 'hide', false)

          if (status === 'pending') {
            field.value = 'Pending Approval'
            field.info =
      `Your 3rd party payout request submitted on ${createdAt} is pending DMT approval.`
          }

          if (status === 'success') {
            field.value = 'Approved'
            field.info =
      'Your 3rd party payout request has been approved.'
          }

          if (status === 'reject') {
            field.value = 'Reject'
            field.info = rejectionReason
              ? `Rejected – ${rejectionReason}`
              : 'Your 3rd party payout request was rejected.'
          }
        }
        // Map existing uploads to the six fields if present
        if (data.uploads && typeof data.uploads === 'object') {
          // consentLetter: backend may store consentLetter under 'consentLetter' or 'consentForm'
          if (data.uploads.consentLetter) {
            // set the read-only display and set upload field value so UI shows uploaded file
            this.thirdPartyDocs.fields.consentLetter.value = this.thirdPartyDocs.fields.consentLetter.value || 'Download Consent Form (PDF): ' + this.consentFormUrl
            if (this.thirdPartyDocs.fields.consentLetterUpload) {
              this.thirdPartyDocs.fields.consentLetterUpload.value = data.uploads.consentLetter
            }
          }
          if (data.uploads.thirdPartyIc) {
            this.thirdPartyDocs.fields.thirdPartyIc.value = data.uploads.thirdPartyIc
          }
          if (data.uploads.witnessIc) {
            this.thirdPartyDocs.fields.witnessIc.value = data.uploads.witnessIc
          }
          if (data.uploads.letterAuthorization) {
            this.thirdPartyDocs.fields.letterAuthorization.value = data.uploads.letterAuthorization
          }
          if (data.uploads.form59) {
            this.thirdPartyDocs.fields.form59.value = data.uploads.form59
          }
          if (data.uploads.ssm) {
            this.thirdPartyDocs.fields.ssm.value = data.uploads.ssm
          }
        }

        if (this.pendingApproval) {
          Object.values(this.selection.fields).forEach(field => {
            field.hide = true
          })

          // show ONLY status info
          this.selection.fields.thirdPartyStatusInfo.hide = false

          // hard lock
          this.submission.submit = false
        }

        this.syncVisibility()
        this.applyPayoutRules()
        this.updateSummary()
        if (data.status === 'reject') {
          this.status = 'error'
          this.alertText = data.rejectionReason
            ? `Rejected – Please resubmit with correct documents. (${data.rejectionReason})`
            : 'Rejected – Please resubmit with correct documents.'
        } else if (this.locked) {
          this.status = 'info'
          this.alertText = 'Bank information updated successfully.'
        }
      } catch (error) {
        // ignore fetch errors for now
      }
    },
    buildUploadPayload () {
      if (!this.requiresThirdParty) return {}

      const uploads = {}
      const fields = this.thirdPartyDocs.fields

      const map = {
        consentLetterUpload: 'consentLetter',
        thirdPartyIc: 'thirdPartyIc',
        witnessIc: 'witnessIc',
        form49: 'form49',
        form51: 'form51',
        form58: 'form58',
        ssm: 'ssm',
      }

      Object.keys(map).forEach(key => {
        const field = fields[key]
        if (!field || !field.value || !field.value.url) return

        uploads[map[key]] = {
          url: field.value.url,
          name: key, // ⭐ always pass fieldKey, not filename
        }
      })

      return uploads
    },

    updateSummary () {
      const label = this.buildPayoutMethodLabel() || 'Not selected'
      const holder = this.requiresThirdParty
        ? `${this.thirdPartyBank.fields.tpName.value || '-'} (${this.thirdPartyBank.fields.tpId.value || '-'})`
        : `${this.ownAccount.fields.ownerName.value || '-'} (${this.ownAccount.fields.ownerId.value || '-'})`

      const bank = this.requiresThirdParty
        ? `${this.getBankNameById(this.thirdPartyBank.fields.tpBankName.value)} / ${this.thirdPartyBank.fields.tpBankAccount.value || '-'}`
        : `${this.getBankNameById(this.ownAccount.fields.bankName.value)} / ${this.ownAccount.fields.bankAccount.value || '-'}`

      // -----------------------------
      // DOCUMENTS (FIXED 6 CATEGORIES)
      // -----------------------------
      const docMap = [
        { key: 'consentLetterUpload', title: 'Consent Letter (signed)' },
        { key: 'thirdPartyIc', title: '3rd Party IC' },
        { key: 'witnessIc', title: 'Witness IC' },
        { key: 'form49', title: 'Form 49' },
        { key: 'form51', title: 'Form 51' },
        { key: 'form58', title: 'Form 58' },
        { key: 'ssm', title: 'SSM' },
      ]

      const docs = docMap
        .map(d => {
          const field = this.thirdPartyDocs.fields[d.key]
          if (!field || !field.value || !field.value.name) return null
          return `• ${d.title}\n  File: ${field.value.name}`
        })
        .filter(Boolean)

      // -----------------------------
      // FINAL SUMMARY TEXT (CASH)
      // -----------------------------
      let summary =
`Payout Method: ${label}

Account Holder: ${holder}

Bank: ${bank}`

      if (this.requiresThirdParty) {
        summary += `

Uploaded Documents:

${docs.join('\n')}`
      }

      this.submission.fields.summary.value = summary
    },
    async submit () {
      if (this.locked && !this.pendingApproval) {
        this.submission.status = 'info'
        this.status = 'info'
        this.alertText = 'Bank information already updated.'
        this.submission.submit = false
        return
      }

      try {
      // Build payload after ensuring uploads are completed and upload fields are populated
        const payload = {
          paymentMethod: this.paymentMethod,
          accountType: this.accountType,
          payoutTo: this.payoutTo,
          bankName: this.ownAccount.fields.bankName.value,
          bankAccount: this.ownAccount.fields.bankAccount.value,
          ownerName: this.ownAccount.fields.ownerName.value,
          ownerId: this.ownAccount.fields.ownerId.value,
          thirdParty: this.requiresThirdParty ? {
            name: this.thirdPartyBank.fields.tpName.value,
            id: this.thirdPartyBank.fields.tpId.value,
            bankName: this.thirdPartyBank.fields.tpBankName.value,
            bankAccount: this.thirdPartyBank.fields.tpBankAccount.value,
          } : null,
          uploads: this.buildUploadPayload(),
          status: this.requiresThirdParty ? 'pending' : 'updated',
          payoutMethodLabel: this.buildPayoutMethodLabel(),
        }

        const params = createPostParams({
          action: 'updatePayoutMethod',
          payload,
        })
        await this.$rest.post('postAction.php', params)
        this.status = 'success'
        this.submission.status = 'success'
        this.alertText = this.requiresThirdParty
          ? 'Your request has been submitted and is pending DMT approval.'
          : 'Bank information updated successfully.'
        this.submission.submit = false
        this.pendingApproval = this.requiresThirdParty
        this.locked = !this.requiresThirdParty
      } catch (error) {
        let message = error.message
        if (error.response && error.response.data) {
          message += ': ' + error.response.data
        }
        this.status = 'error'
        this.submission.status = 'error'
        this.submission.errorMessage = message
        this.alertText = message
        this.submission.submit = false
      }
    },

    buildPayoutMethodLabel () {
      if (this.accountType === 'individual' && this.payoutTo === 'own') { return 'Cash – Individual – Own Account' }

      if (this.accountType === 'individual' && this.payoutTo === 'third_party') { return 'Cash – Individual – 3rd Party Account' }

      if (this.accountType === 'company' && this.payoutTo === 'own') { return 'Cash – Company – Own Account' }

      if (this.accountType === 'company' && this.payoutTo === 'third_party') { return 'Cash – Company – 3rd Party Account' }

      return 'Cash'
    },
  },
}
